// SO bill-to / ship-to override blocks — web editor UI (Task 5).
// Emits the EXACT request-contract keys of backend/src/modules/sales/so-address-override.ts
// (billToBlockSchema / shipToBlockSchema): useOverride, savedAddressId?, label?, name,
// phone?, gstin? (bill only, 15 chars), stateCode? (bill only, 2 chars), address,
// saveToCustomer, makeDefault. A side is omitted entirely when its box is unticked.
import { useState } from 'react';
import { api } from '@/utils/api';

export interface OverrideAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  pincode: string;
  country?: string;
}

export interface BillToBlock {
  useOverride: true;
  savedAddressId?: string;
  label?: string;
  name: string;
  phone?: string;
  gstin?: string;
  stateCode?: string;
  address: OverrideAddress;
  saveToCustomer: boolean;
  makeDefault: boolean;
}

export interface ShipToBlock {
  useOverride: true;
  savedAddressId?: string;
  label?: string;
  name: string;
  phone?: string;
  address: OverrideAddress;
  saveToCustomer: boolean;
  makeDefault: boolean;
}

export interface BillShipValue {
  billTo?: BillToBlock;
  shipTo?: ShipToBlock;
}

interface SavedAddress {
  id: string;
  label?: string;
  name: string;
  phone?: string | null;
  gstin?: string | null;
  stateCode?: string | null;
  address?: Partial<OverrideAddress> | null;
  isDefaultBill?: boolean;
  isDefaultShip?: boolean;
}

interface SideFields {
  savedAddressId: string;
  label: string;
  name: string;
  phone: string;
  gstin: string;
  stateCode: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  saveToCustomer: boolean;
  makeDefault: boolean;
}

const emptyFields = (): SideFields => ({
  savedAddressId: '',
  label: '',
  name: '',
  phone: '',
  gstin: '',
  stateCode: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  pincode: '',
  country: 'India',
  saveToCustomer: true,
  makeDefault: false,
});

const STATES = [
  { code: '01', name: 'Jammu & Kashmir' }, { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' }, { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' }, { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' }, { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' }, { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' }, { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' }, { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' }, { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' }, { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' }, { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' }, { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' }, { code: '24', name: 'Gujarat' },
  { code: '25', name: 'Daman & Diu' }, { code: '26', name: 'Dadra & Nagar Haveli' },
  { code: '27', name: 'Maharashtra' }, { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' }, { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' }, { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' }, { code: '35', name: 'Andaman & Nicobar' },
  { code: '36', name: 'Telangana' }, { code: '37', name: 'Andhra Pradesh' },
];

/** Buyer state for the tax chip: GSTIN's first 2 digits when 15 chars, else typed stateCode. */
function deriveStateCode(gstin: string, stateCode: string): string {
  if (gstin && gstin.trim().length === 15) return gstin.trim().slice(0, 2);
  if (stateCode && stateCode.length === 2) return stateCode;
  return '';
}

function fieldsFromSaved(a: SavedAddress): SideFields {
  const addr = a.address || {};
  return {
    savedAddressId: a.id,
    label: a.label || '',
    name: a.name || '',
    phone: a.phone || '',
    gstin: a.gstin || '',
    stateCode: a.stateCode || '',
    line1: addr.line1 || '',
    line2: addr.line2 || '',
    city: addr.city || '',
    state: addr.state || '',
    pincode: addr.pincode || '',
    country: addr.country || 'India',
    saveToCustomer: true,
    makeDefault: false,
  };
}

function buildAddress(f: SideFields): OverrideAddress {
  return {
    line1: f.line1,
    ...(f.line2 ? { line2: f.line2 } : {}),
    city: f.city,
    state: f.state,
    pincode: f.pincode,
    ...(f.country ? { country: f.country } : {}),
  };
}

function buildBillBlock(f: SideFields): BillToBlock {
  const derived = deriveStateCode(f.gstin, f.stateCode);
  return {
    useOverride: true,
    ...(f.savedAddressId ? { savedAddressId: f.savedAddressId } : {}),
    ...(f.label ? { label: f.label } : {}),
    name: f.name,
    ...(f.phone ? { phone: f.phone } : {}),
    ...(f.gstin.trim().length === 15 ? { gstin: f.gstin.trim().toUpperCase() } : {}),
    ...(derived ? { stateCode: derived } : {}),
    address: buildAddress(f),
    saveToCustomer: f.saveToCustomer,
    makeDefault: f.makeDefault,
  };
}

function buildShipBlock(f: SideFields): ShipToBlock {
  return {
    useOverride: true,
    ...(f.savedAddressId ? { savedAddressId: f.savedAddressId } : {}),
    ...(f.label ? { label: f.label } : {}),
    name: f.name,
    ...(f.phone ? { phone: f.phone } : {}),
    address: buildAddress(f),
    saveToCustomer: f.saveToCustomer,
    makeDefault: f.makeDefault,
  };
}

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none';

export function BillShipOverride({
  accountId,
  value,
  onChange,
  sellerState,
}: {
  accountId: string;
  value: BillShipValue;
  onChange: (v: BillShipValue) => void;
  sellerState: string;
}) {
  const [billEnabled, setBillEnabled] = useState<boolean>(!!value.billTo);
  const [shipEnabled, setShipEnabled] = useState<boolean>(!!value.shipTo);
  const [billFields, setBillFields] = useState<SideFields>(emptyFields());
  const [shipFields, setShipFields] = useState<SideFields>(emptyFields());
  const [billBook, setBillBook] = useState<SavedAddress[]>([]);
  const [shipBook, setShipBook] = useState<SavedAddress[]>([]);

  const emit = (opts: { bill?: boolean; ship?: boolean; bf?: SideFields; sf?: SideFields }) => {
    const bill = opts.bill ?? billEnabled;
    const ship = opts.ship ?? shipEnabled;
    const bf = opts.bf ?? billFields;
    const sf = opts.sf ?? shipFields;
    onChange({
      ...(bill ? { billTo: buildBillBlock(bf) } : {}),
      ...(ship ? { shipTo: buildShipBlock(sf) } : {}),
    });
  };

  const fetchBook = async (kind: 'bill' | 'ship') => {
    if (!accountId) return;
    try {
      const res = await api.get(`/accounts/${accountId}/addresses?kind=${kind}`);
      const d = res.data?.data ?? res.data ?? {};
      const addresses: SavedAddress[] = d.addresses ?? [];
      const defaultId = kind === 'bill' ? d.defaultBillId : d.defaultShipId;
      if (kind === 'bill') {
        setBillBook(addresses);
        const def = addresses.find((a) => a.id === defaultId) ?? addresses[0];
        if (def) {
          const f = fieldsFromSaved(def);
          setBillFields(f);
          emit({ bill: true, bf: f });
        }
      } else {
        setShipBook(addresses);
        const def = addresses.find((a) => a.id === defaultId) ?? addresses[0];
        if (def) {
          const f = fieldsFromSaved(def);
          setShipFields(f);
          emit({ ship: true, sf: f });
        }
      }
    } catch {
      /* address book unavailable — manual entry still works */
    }
  };

  const toggleSide = (kind: 'bill' | 'ship', checked: boolean) => {
    if (kind === 'bill') {
      setBillEnabled(checked);
      emit({ bill: checked });
      if (checked) void fetchBook('bill');
    } else {
      setShipEnabled(checked);
      emit({ ship: checked });
      if (checked) void fetchBook('ship');
    }
  };

  const updateSide = (kind: 'bill' | 'ship', patch: Partial<SideFields>) => {
    if (kind === 'bill') {
      const f = { ...billFields, ...patch };
      setBillFields(f);
      emit({ bf: f });
    } else {
      const f = { ...shipFields, ...patch };
      setShipFields(f);
      emit({ sf: f });
    }
  };

  const pickSaved = (kind: 'bill' | 'ship', id: string) => {
    const book = kind === 'bill' ? billBook : shipBook;
    const row = book.find((a) => a.id === id);
    const f = row ? fieldsFromSaved(row) : { ...emptyFields() };
    if (kind === 'bill') {
      setBillFields(f);
      emit({ bf: f });
    } else {
      setShipFields(f);
      emit({ sf: f });
    }
  };

  const renderFields = (kind: 'bill' | 'ship') => {
    const f = kind === 'bill' ? billFields : shipFields;
    const book = kind === 'bill' ? billBook : shipBook;
    const P = kind === 'bill' ? 'Bill-to' : 'Ship-to';
    const derived = kind === 'bill' ? deriveStateCode(f.gstin, f.stateCode) : '';
    const sameState = derived && derived === sellerState;
    return (
      <div className="mt-3 space-y-2">
        {book.length > 0 && (
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Saved addresses</label>
            <select
              aria-label={`${P} saved address`}
              value={f.savedAddressId}
              onChange={(e) => pickSaved(kind, e.target.value)}
              className={inputCls}
            >
              <option value="">&#10133; Add new</option>
              {book.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label || a.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            aria-label={`${P} label`}
            placeholder="Label (e.g. Delhi HO)"
            value={f.label}
            onChange={(e) => updateSide(kind, { label: e.target.value })}
            className={inputCls}
          />
          <input
            type="text"
            aria-label={`${P} name`}
            placeholder="Name / Company"
            value={f.name}
            onChange={(e) => updateSide(kind, { name: e.target.value })}
            className={inputCls}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          {kind === 'bill' && (
            <input
              type="text"
              aria-label="Bill-to GSTIN"
              placeholder="GSTIN (optional)"
              value={f.gstin}
              onChange={(e) => updateSide(kind, { gstin: e.target.value.toUpperCase() })}
              className={inputCls}
            />
          )}
          <input
            type="text"
            aria-label={`${P} phone`}
            placeholder="Phone"
            value={f.phone}
            onChange={(e) => updateSide(kind, { phone: e.target.value })}
            className={inputCls}
          />
        </div>
        {kind === 'bill' && (
          <div className="flex items-center gap-2">
            <select
              aria-label="Bill-to state code"
              value={f.stateCode}
              onChange={(e) => updateSide(kind, { stateCode: e.target.value })}
              className={inputCls}
            >
              <option value="">State (for GST when no GSTIN)</option>
              {STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} - {s.name}
                </option>
              ))}
            </select>
            {derived && (
              <span
                className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${
                  sameState ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                }`}
              >
                {sameState ? 'Same state → CGST+SGST' : 'Inter-state → IGST'}
              </span>
            )}
          </div>
        )}
        <input
          type="text"
          aria-label={`${P} address line 1`}
          placeholder="Address Line 1"
          value={f.line1}
          onChange={(e) => updateSide(kind, { line1: e.target.value })}
          className={inputCls}
        />
        <input
          type="text"
          aria-label={`${P} address line 2`}
          placeholder="Address Line 2"
          value={f.line2}
          onChange={(e) => updateSide(kind, { line2: e.target.value })}
          className={inputCls}
        />
        <div className="grid grid-cols-3 gap-2">
          <input
            type="text"
            aria-label={`${P} city`}
            placeholder="City"
            value={f.city}
            onChange={(e) => updateSide(kind, { city: e.target.value })}
            className={inputCls}
          />
          <input
            type="text"
            aria-label={`${P} state`}
            placeholder="State"
            value={f.state}
            onChange={(e) => updateSide(kind, { state: e.target.value })}
            className={inputCls}
          />
          <input
            type="text"
            aria-label={`${P} pincode`}
            placeholder="Pincode"
            value={f.pincode}
            onChange={(e) => updateSide(kind, { pincode: e.target.value })}
            className={inputCls}
          />
        </div>
        <div className="flex items-center gap-4 pt-1">
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              aria-label={`${P} save to customer`}
              checked={f.saveToCustomer}
              onChange={(e) => updateSide(kind, { saveToCustomer: e.target.checked })}
              className="rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]"
            />
            Save to customer for reuse
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              aria-label={`${P} set as default`}
              checked={f.makeDefault}
              onChange={(e) => updateSide(kind, { makeDefault: e.target.checked })}
              className="rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]"
            />
            Set as default
          </label>
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <h2 className="mb-4 text-sm font-semibold text-gray-700 uppercase tracking-wider">
        Bill To / Ship To Override
      </h2>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              aria-label="Different billing details & address"
              checked={billEnabled}
              onChange={(e) => toggleSide('bill', e.target.checked)}
              className="rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]"
            />
            Different billing details &amp; address
          </label>
          {billEnabled && renderFields('bill')}
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              aria-label="Different shipping details & address"
              checked={shipEnabled}
              onChange={(e) => toggleSide('ship', e.target.checked)}
              className="rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]"
            />
            Different shipping details &amp; address
          </label>
          {shipEnabled && renderFields('ship')}
        </div>
      </div>
    </div>
  );
}
